import React, { useMemo } from 'react';
import { Box, Button, IconButton, Slider, Typography, useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
type CanvasScaleControlProps = {
	scale: number;
	minScale: number;
	maxScale: number;
	onScaleChange: (nextScale: number) => void;
	step?: number;
};

function CanvasScaleControl({
	scale,
	minScale,
	maxScale,
	onScaleChange,
	step = 0.1,
}: CanvasScaleControlProps) {
	const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
	const scalePercent = useMemo(() => Math.round(scale * 100), [scale]);

	const handleSliderChange = (_event: Event, value: number | number[]) => {
		const nextScale = Array.isArray(value) ? value[0] : value;
		onScaleChange(nextScale);
	};

	return (
		
		isMobile ? (
			<Box
				sx={{
					position: 'absolute',
					left: 0,
					bottom: 40,
					zIndex: (theme) => theme.zIndex.fab,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: 1,
					px: 0,
					pointerEvents: 'auto',
					userSelect: 'none',
				}}
				onClick={(e) => e.stopPropagation()}
				onPointerDown={(e) => e.stopPropagation()}
			>
				<IconButton
					size='small'
					aria-label='Zoom in canvas'
					onClick={() => onScaleChange(scale + step)}
					sx={{ color: 'text.secondary' }}
					tabIndex={-1}
				>
					<ZoomInIcon fontSize='small' />
				</IconButton>
				<Button variant='text' size='small' onClick={() => onScaleChange(1)} tabIndex={-1}>
					<Typography variant='body2' color='text.secondary' sx={{ minWidth: 44, textAlign: 'center' }}>
						{`${scalePercent}%`}
					</Typography>
				</Button>
				<IconButton
					size='small'
					aria-label='Zoom out canvas'
					onClick={() => onScaleChange(scale - step)}
					sx={{ color: 'text.secondary' }}
					tabIndex={-1}
				>
					<ZoomOutIcon fontSize='small' />
				</IconButton>
			</Box>
		) : (
			<Box
				sx={{
					position: 'absolute',
					left: 16,
					bottom: 32,
					zIndex: (theme) => theme.zIndex.fab,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: 1.5,
					py: 0.5,
					pointerEvents: 'auto',
					userSelect: 'none',
					opacity: 0.5,
					'&:hover': {
						opacity: 1,
					},
				}}
				onClick={(e) => e.stopPropagation()}
				onPointerDown={(e) => e.stopPropagation()}
			>
				<IconButton size='small' onClick={() => onScaleChange(1)} tabIndex={-1}><ZoomInIcon /></IconButton>
				<Slider
					orientation='vertical'
					min={minScale}
					max={maxScale}
					step={0.01}
					value={scale}
					onChange={handleSliderChange}
					aria-label='Canvas horizontal scale'
					tabIndex={-1}
					sx={{
						height: 100,
						'& .MuiSlider-thumb': { width: 14, height: 14 },
					}}
				/>
				<Typography variant='caption' color='text.secondary' sx={{ minWidth: 36, textAlign: 'right' }}>
					{`${Math.round(scale * 100)}%`}
				</Typography>
			</Box>
		)
	);
}

export default CanvasScaleControl;
